/**
 * Agent API Client (Agent portal operations)
 */
import { ApiError, NetworkError, AuthenticationError, NotFoundError } from './errors.js';
export class AgentClient {
    baseUrl;
    getToken;
    debug;
    requestTimeoutMs = 30_000;
    constructor(options) {
        this.baseUrl = options.baseUrl.replace(/\/$/, '');
        this.getToken = options.getToken ?? (async () => null);
        this.debug = options.debug ?? false;
    }
    /**
     * Set the token getter function
     */
    setTokenGetter(getToken) {
        this.getToken = getToken;
    }
    async request(path, options = {}) {
        const timeoutMs = this.requestTimeoutMs;
        // Firebase getIdToken() and fetch() can both hang silently (token refresh
        // stall, worker cold-start blip). Without a deadline the caller's await
        // never resolves *or* rejects, so UI code stuck in an `await` never gets
        // to run its catch/finally — leaving e.g. a submit button disabled forever.
        let tokenTimeoutId;
        const tokenTimeout = new Promise((_, reject) => {
            tokenTimeoutId = setTimeout(() => reject(new NetworkError(`Auth token fetch timed out after ${timeoutMs}ms`)), timeoutMs);
        });
        let token;
        try {
            token = await Promise.race([this.getToken(), tokenTimeout]);
        }
        finally {
            if (tokenTimeoutId !== undefined)
                clearTimeout(tokenTimeoutId);
        }
        if (!token) {
            throw new AuthenticationError('No authentication token');
        }
        const url = `${this.baseUrl}${path}`;
        if (this.debug) {
            console.log(`[AgentClient] ${options.method ?? 'GET'} ${url}`);
        }
        const controller = new AbortController();
        const fetchTimeoutId = setTimeout(() => controller.abort(), timeoutMs);
        try {
            const response = await fetch(url, {
                ...options,
                signal: controller.signal,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                    ...options.headers,
                },
            });
            const data = (await response.json());
            if (!response.ok) {
                if (response.status === 401) {
                    throw new AuthenticationError(data.error?.message);
                }
                if (response.status === 404) {
                    throw new NotFoundError(data.error?.message);
                }
                throw ApiError.fromResponse(response.status, data.error ?? {});
            }
            if (!data.success) {
                throw ApiError.fromResponse(response.status, data.error ?? {});
            }
            return data.data;
        }
        catch (error) {
            if (error instanceof ApiError) {
                throw error;
            }
            if (error?.name === 'AbortError') {
                throw new NetworkError(`Request timed out after ${timeoutMs}ms: ${options.method ?? 'GET'} ${path}`);
            }
            throw new NetworkError(error instanceof Error ? error.message : 'Network error');
        }
        finally {
            clearTimeout(fetchTimeoutId);
        }
    }
    // Auth / Registration
    /**
     * Register or update agent on sign-in.
     * Updates last_login if this agent already has a record. On first sign-in
     * for a brand-new agent, the server requires `licenseNumber` and
     * `subdomain` and throws an ApiError with code 'LICENSE_REQUIRED' if
     * they're omitted (subdomain validity/uniqueness errors come back as
     * 'SUBDOMAIN_REQUIRED'/'SUBDOMAIN_TAKEN') - callers should call this once
     * with no arguments, catch that specific error, collect the info, and
     * retry with `info` filled in. The claimed subdomain becomes the agent's
     * actual subdomain immediately (not just a stored preference), so it's
     * already there as the default when admin approves.
     */
    async register(info) {
        return this.request('/auth/register', {
            method: 'POST',
            ...(info ? { body: JSON.stringify(info) } : {}),
        });
    }
    /**
     * Get current agent's auth status and profile.
     */
    async getAuthStatus() {
        return this.request('/auth/me');
    }
    /**
     * Resubmit a rejected application with corrected info, putting it back
     * into 'pending' for another admin review. Only valid when the agent's
     * current status is 'rejected' - the server returns a 409 otherwise.
     */
    async resubmit(info) {
        return this.request('/auth/resubmit', {
            method: 'POST',
            body: JSON.stringify(info),
        });
    }
    /**
     * Update agent's profile fields. `subdomain` only works as a first-time
     * claim during onboarding - the server rejects it once the agent already
     * has one set, since changing an existing subdomain is a much bigger
     * operation (breaks existing R2 keys/URLs/the KAM cookie) that isn't
     * supported through this endpoint.
     */
    async updateAuthProfile(updates) {
        return this.request('/auth/profile', {
            method: 'PATCH',
            body: JSON.stringify(updates),
        });
    }
    /**
     * Update the agent.slowmaui.com landing page config. storyHtml is
     * sanitized server-side (allowlist-filtered) before storage - what comes
     * back from getAuthStatus() is always safe to render as-is.
     */
    async updateLandingPage(updates) {
        return this.request('/auth/landing-page', {
            method: 'PATCH',
            body: JSON.stringify(updates),
        });
    }
    /**
     * Upload an image or video (agent photo, broker logo, landing page
     * background image, or landing page background video) to the agent's own
     * subdomain storage. Returns a cache-busted URL (a fresh ?v= on every
     * upload) since the underlying storage key is fixed per kind and would
     * otherwise be served stale from cache for up to an hour after a
     * re-upload.
     */
    async uploadAgentImage(kind, file) {
        return this.request(`/auth/upload/${kind}`, {
            method: 'POST',
            headers: { 'Content-Type': file.type || 'application/octet-stream' },
            body: file,
        });
    }
    /**
     * Address-substring search over currently active listings, for the
     * landing page's "featured listings" picker.
     */
    async searchListings(query) {
        return this.request(`/listings?q=${encodeURIComponent(query)}`);
    }
    // Stats
    async getStats() {
        return this.request('/stats');
    }
    // Leads (legacy alias — /leads is now a thin redirect to /contacts?status=lead)
    async getLeads(params) {
        const searchParams = new URLSearchParams();
        if (params?.page)
            searchParams.set('page', params.page.toString());
        if (params?.limit)
            searchParams.set('limit', params.limit.toString());
        if (params?.status)
            searchParams.set('status', params.status);
        if (params?.type)
            searchParams.set('type', params.type);
        const query = searchParams.toString();
        return this.request(`/leads${query ? `?${query}` : ''}`);
    }
    async updateLead(id, updates) {
        return this.request(`/leads/${encodeURIComponent(id)}`, {
            method: 'PUT',
            body: JSON.stringify(updates),
        });
    }
    // Contacts (Phase 2 — unified list of agent-owned contacts:
    // form submitters + signed-in users via the public app).
    async listContacts(params) {
        const sp = new URLSearchParams();
        if (params?.page)
            sp.set('page', String(params.page));
        if (params?.limit)
            sp.set('limit', String(params.limit));
        if (params?.status)
            sp.set('status', params.status);
        if (params?.search)
            sp.set('search', params.search);
        if (params?.sort)
            sp.set('sort', params.sort);
        if (params?.order)
            sp.set('order', params.order);
        const q = sp.toString();
        return this.request(`/contacts${q ? `?${q}` : ''}`);
    }
    /**
     * Download contacts as CSV, scoped to the same status/search filters as
     * listContacts (so "Export" grabs whatever the agent currently has
     * filtered/searched, not necessarily the whole book). Bypasses the
     * shared JSON-envelope `request()` helper below since the response body
     * is raw CSV text, not `{success, data}`.
     */
    async exportContactsCsv(params) {
        const sp = new URLSearchParams();
        if (params?.status)
            sp.set('status', params.status);
        if (params?.search)
            sp.set('search', params.search);
        const q = sp.toString();
        const token = await this.getToken();
        if (!token)
            throw new AuthenticationError('No authentication token');
        const response = await fetch(`${this.baseUrl}/contacts/export${q ? `?${q}` : ''}`, {
            headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!response.ok) {
            throw new ApiError('EXPORT_FAILED', `Export failed (${response.status})`, response.status);
        }
        return response.text();
    }
    /**
     * Bulk-import already-parsed CSV rows. Dedup is by email - a row whose
     * email matches an existing contact merges into it (see the worker's
     * handleContactsImport for the exact merge rule) rather than creating a
     * duplicate.
     */
    async importContacts(rows) {
        return this.request('/contacts/import', {
            method: 'POST',
            body: JSON.stringify({ rows }),
        });
    }
    async getContact(id) {
        return this.request(`/contacts/${encodeURIComponent(id)}`);
    }
    async updateContact(id, updates) {
        return this.request(`/contacts/${encodeURIComponent(id)}`, {
            method: 'PATCH',
            body: JSON.stringify(updates),
        });
    }
    async addContactNote(id, body) {
        return this.request(`/contacts/${encodeURIComponent(id)}/notes`, {
            method: 'POST',
            body: JSON.stringify({ body }),
        });
    }
    /**
     * Set an alert for this contact - a real `user_alerts` row if they
     * already have an account, a `pending_alerts` row otherwise (converted
     * automatically on their next sign-in). `pending` in the response tells
     * the caller which kind it wrote.
     */
    async addContactAlert(id, params) {
        return this.request(`/contacts/${encodeURIComponent(id)}/alerts`, {
            method: 'POST',
            body: JSON.stringify(params),
        });
    }
    async removeContactAlert(id, itemId) {
        return this.request(`/contacts/${encodeURIComponent(id)}/alerts/${encodeURIComponent(itemId)}`, {
            method: 'DELETE',
        });
    }
    /**
     * Send an email to a contact. Two shapes share this endpoint:
     *   - Reply: pass `subject` + `message`. No listing.
     *   - Property share (A-M2): pass `listingId` (+ optional `message`); the
     *     worker fetches the listing and appends a property card to the body.
     *
     * `listingId` accepts either the ListingKey hash (`mls_listings.id`) or the
     * MLS number (`mls_listings.listing_id`) — same convention as the public
     * `?p=` deep-link. At least one of `listingId` or `message` must be set.
     */
    async sendContactEmail(contactId, payload) {
        return this.request(`/contacts/${encodeURIComponent(contactId)}/email`, {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    }
    // Dashboard
    async getDashboardStats() {
        return this.request('/dashboard/stats');
    }
    async getActivity(params) {
        const searchParams = new URLSearchParams();
        if (params?.limit)
            searchParams.set('limit', params.limit.toString());
        const query = searchParams.toString();
        return this.request(`/activity${query ? `?${query}` : ''}`);
    }
    // Recent MLS changes (new listings, price changes, pending, sold),
    // islandwide - not scoped to this agent's published SEO pages. Same item
    // shape as the public data-api ActivityItem, plus districtId/regionId for
    // the Region/District/Neighborhood cascade filter. Separate from
    // getActivity (site engagement) - different data, different shape.
    async getMarketActivity(params) {
        const searchParams = new URLSearchParams();
        if (params?.days)
            searchParams.set('days', params.days.toString());
        if (params?.limit)
            searchParams.set('limit', params.limit.toString());
        const query = searchParams.toString();
        return this.request(`/market-activity${query ? `?${query}` : ''}`);
    }
    // Neighborhoods (for content/sharing)
    async getNeighborhoods(params) {
        const searchParams = new URLSearchParams();
        if (params?.limit)
            searchParams.set('limit', params.limit.toString());
        const query = searchParams.toString();
        return this.request(`/neighborhoods${query ? `?${query}` : ''}`);
    }
    // Geography Tree
    async getGeographyTree() {
        return this.request('/geography/tree');
    }
    async getGeographyEntity(entityType, entityId) {
        return this.request(`/geography/${entityType}/${entityId}`);
    }
    // Content Management
    async getContent(entityType, entityId) {
        return this.request(`/content/${entityType}/${entityId}`);
    }
    async getContentSection(entityType, entityId, section) {
        return this.request(`/content/${entityType}/${entityId}/${section}`);
    }
    async saveContent(params) {
        return this.request(`/content/${params.entityType}/${params.entityId}/${params.section}`, {
            method: 'PUT',
            body: JSON.stringify({
                content: params.content,
                is_published: params.is_published ?? false,
                is_approved: params.is_approved,
                save_as_default: params.save_as_default ?? false,
            }),
        });
    }
    async deleteContent(entityType, entityId, section) {
        return this.request(`/content/${entityType}/${entityId}/${section}`, { method: 'DELETE' });
    }
    async approveContent(params) {
        return this.request(`/content/${params.entityType}/${params.entityId}/approve`, {
            method: 'POST',
            body: JSON.stringify({
                section: params.section,
                is_approved: params.is_approved,
            }),
        });
    }
    async generateContent(params) {
        return this.request(`/content/${params.entityType}/${params.entityId}/generate`, {
            method: 'POST',
            body: JSON.stringify({ section: params.section }),
        });
    }
    // Content Suggestions - non-admin agents propose changes to default
    // content; admins review and accept/reject. See migration 042.
    async getContentSuggestions(params) {
        const query = new URLSearchParams();
        if (params?.entityType)
            query.set('entityType', params.entityType);
        if (params?.entityId)
            query.set('entityId', params.entityId);
        if (params?.section)
            query.set('section', params.section);
        if (params?.status)
            query.set('status', params.status);
        const qs = query.toString();
        return this.request(`/content-suggestions${qs ? `?${qs}` : ''}`);
    }
    async createContentSuggestion(params) {
        return this.request('/content-suggestions', {
            method: 'POST',
            body: JSON.stringify({
                entityType: params.entityType,
                entityId: params.entityId,
                section: params.section,
                suggestedContent: params.suggestedContent,
                note: params.note,
            }),
        });
    }
    async acceptContentSuggestion(id) {
        return this.request(`/content-suggestions/${id}/accept`, {
            method: 'POST',
        });
    }
    async rejectContentSuggestion(id, adminNote) {
        return this.request(`/content-suggestions/${id}/reject`, {
            method: 'POST',
            body: JSON.stringify({ adminNote }),
        });
    }
    // Agent Notes ("Agent Insights") - additive per-agent notes on an
    // entity, managed from the agent portal. Moderated like content
    // suggestions since migration 050 - see Phase 2 of
    // docs/plans/agent-content-workflow-2026-09-28.md.
    async getAgentNotes(params) {
        const query = new URLSearchParams();
        if (params?.entityType)
            query.set('entityType', params.entityType);
        if (params?.entityId)
            query.set('entityId', params.entityId);
        if (params?.status)
            query.set('status', params.status);
        const qs = query.toString();
        return this.request(`/agent-notes${qs ? `?${qs}` : ''}`);
    }
    async createAgentNote(params) {
        return this.request('/agent-notes', {
            method: 'POST',
            body: JSON.stringify(params),
        });
    }
    async updateAgentNote(id, noteHtml) {
        return this.request(`/agent-notes/${id}`, {
            method: 'PUT',
            body: JSON.stringify({ noteHtml }),
        });
    }
    async approveAgentNote(id) {
        return this.request(`/agent-notes/${id}/approve`, {
            method: 'POST',
        });
    }
    async rejectAgentNote(id, adminNote) {
        return this.request(`/agent-notes/${id}/reject`, {
            method: 'POST',
            body: JSON.stringify({ adminNote }),
        });
    }
    async deleteAgentNote(id) {
        return this.request(`/agent-notes/${id}`, {
            method: 'DELETE',
        });
    }
    // Agent Stories (Buyer/Seller Messages) - compile mode's backing CRUD.
    // See migration 046 and workers/agent-api's handleAgentStories/
    // handleStoryItems. Sending (POST /agent-stories/:id/send) isn't wrapped
    // here yet - that's Phase 4, once the rich email template exists.
    async getAgentStories(params) {
        const query = new URLSearchParams();
        if (params?.status)
            query.set('status', params.status);
        if (params?.storyType)
            query.set('storyType', params.storyType);
        const qs = query.toString();
        return this.request(`/agent-stories${qs ? `?${qs}` : ''}`);
    }
    async getAgentStory(id) {
        return this.request(`/agent-stories/${id}`);
    }
    async createAgentStory(params) {
        return this.request('/agent-stories', {
            method: 'POST',
            body: JSON.stringify(params),
        });
    }
    async updateAgentStory(id, params) {
        return this.request(`/agent-stories/${id}`, {
            method: 'PUT',
            body: JSON.stringify(params),
        });
    }
    async deleteAgentStory(id) {
        return this.request(`/agent-stories/${id}`, {
            method: 'DELETE',
        });
    }
    async addStoryItem(storyId, params) {
        return this.request(`/agent-stories/${storyId}/items`, {
            method: 'POST',
            body: JSON.stringify(params),
        });
    }
    async updateStoryItem(storyId, itemId, params) {
        return this.request(`/agent-stories/${storyId}/items/${itemId}`, {
            method: 'PUT',
            body: JSON.stringify(params),
        });
    }
    async removeStoryItem(storyId, itemId) {
        return this.request(`/agent-stories/${storyId}/items/${itemId}`, {
            method: 'DELETE',
        });
    }
    /** The exact HTML Send will mail (minus the per-recipient greeting) -
     *  see agent-api's buildStoryBodyHtml, shared by both routes so preview
     *  can never drift from what actually goes out. */
    async getStoryPreview(id) {
        return this.request(`/agent-stories/${id}/preview`);
    }
    // Replaces the old {contactIds, tag} shape (per Todd 2026-10-29) with
    // familiar To/Cc/Bcc fields - a contactId-less entry is a freeform-
    // typed address not yet in the agent's contacts, auto-promoted into a
    // real contact row server-side at send time. See handleStorySend for
    // the per-type delivery mechanics (private personalized copies for
    // Buyer/Seller's To+Cc, one real shared email for General's To+Cc,
    // chunked Bcc blasts for all three).
    async sendStory(id, params) {
        return this.request(`/agent-stories/${id}/send`, {
            method: 'POST',
            body: JSON.stringify(params),
        });
    }
    // Agent Carts - the neutral "collect first, decide later" counterpart
    // to Stories above. See migration 057 and workers/agent-api's
    // handleAgentCarts/handleCartItems. No preview/send/publish - a cart's
    // items only ever leave it by being copied into a Story via addStoryItem.
    async getAgentCarts() {
        return this.request('/agent-carts');
    }
    async getAgentCart(id) {
        return this.request(`/agent-carts/${id}`);
    }
    async createAgentCart(params) {
        return this.request('/agent-carts', {
            method: 'POST',
            body: JSON.stringify(params ?? {}),
        });
    }
    async updateAgentCart(id, params) {
        return this.request(`/agent-carts/${id}`, {
            method: 'PUT',
            body: JSON.stringify(params),
        });
    }
    async deleteAgentCart(id) {
        return this.request(`/agent-carts/${id}`, {
            method: 'DELETE',
        });
    }
    async addCartItem(cartId, params) {
        return this.request(`/agent-carts/${cartId}/items`, {
            method: 'POST',
            body: JSON.stringify(params),
        });
    }
    async updateCartItem(cartId, itemId, params) {
        return this.request(`/agent-carts/${cartId}/items/${itemId}`, {
            method: 'PUT',
            body: JSON.stringify(params),
        });
    }
    async removeCartItem(cartId, itemId) {
        return this.request(`/agent-carts/${cartId}/items/${itemId}`, {
            method: 'DELETE',
        });
    }
    // Contact Lists (migration 071) - structural twin of Agent Carts above,
    // for contacts instead of properties. See workers/agent-api's
    // handleAgentLists/handleListMembers.
    async getAgentLists() {
        return this.request('/agent-lists');
    }
    async getAgentList(id) {
        return this.request(`/agent-lists/${id}`);
    }
    async createAgentList(params) {
        return this.request('/agent-lists', {
            method: 'POST',
            body: JSON.stringify(params ?? {}),
        });
    }
    async updateAgentList(id, params) {
        return this.request(`/agent-lists/${id}`, {
            method: 'PUT',
            body: JSON.stringify(params),
        });
    }
    async deleteAgentList(id) {
        return this.request(`/agent-lists/${id}`, {
            method: 'DELETE',
        });
    }
    async addListMember(listId, params) {
        return this.request(`/agent-lists/${listId}/members`, {
            method: 'POST',
            body: JSON.stringify(params),
        });
    }
    async removeListMember(listId, contactId) {
        return this.request(`/agent-lists/${listId}/members/${contactId}`, {
            method: 'DELETE',
        });
    }
    // Agent Pages (migration 063) - block-based page builder for
    // <agent>.slowmaui.com. See workers/agent-api's handleAgentPages/
    // handleAgentPageBlocks for the backend shape these mirror.
    async getAgentPages() {
        return this.request('/agent-pages');
    }
    async getAgentPage(id) {
        return this.request(`/agent-pages/${id}`);
    }
    async createAgentPage(params) {
        return this.request('/agent-pages', {
            method: 'POST',
            body: JSON.stringify(params),
        });
    }
    async updateAgentPage(id, params) {
        return this.request(`/agent-pages/${id}`, {
            method: 'PATCH',
            body: JSON.stringify(params),
        });
    }
    async deleteAgentPage(id) {
        return this.request(`/agent-pages/${id}`, {
            method: 'DELETE',
        });
    }
    /** Byte-identical to what Publish writes to R2 - see getAgentPagePreview's
     *  callers for how it's opened (new tab via a blob: URL). */
    async getAgentPagePreview(id) {
        return this.request(`/agent-pages/${id}/preview`);
    }
    async publishAgentPage(id) {
        return this.request(`/agent-pages/${id}/publish`, { method: 'PUT' });
    }
    async unpublishAgentPage(id) {
        return this.request(`/agent-pages/${id}/unpublish`, { method: 'PUT' });
    }
    async makeAgentPageHome(id) {
        return this.request(`/agent-pages/${id}/make-home`, { method: 'PUT' });
    }
    async addPageBlock(pageId, params) {
        return this.request(`/agent-pages/${pageId}/blocks`, {
            method: 'POST',
            body: JSON.stringify(params),
        });
    }
    async updatePageBlock(pageId, blockId, params) {
        return this.request(`/agent-pages/${pageId}/blocks/${blockId}`, {
            method: 'PATCH',
            body: JSON.stringify(params),
        });
    }
    async deletePageBlock(pageId, blockId) {
        return this.request(`/agent-pages/${pageId}/blocks/${blockId}`, {
            method: 'DELETE',
        });
    }
    // Media API - Manage media items in media table
    async getMedia(entityType, entityId) {
        return this.request(`/media/${entityType}/${entityId}`);
    }
    async addMedia(params) {
        return this.request(`/media/${params.entityType}/${params.entityId}`, {
            method: 'POST',
            body: JSON.stringify({
                url: params.url,
                caption: params.caption,
                category: params.category,
                source: params.source || 'upload',
                source_data: params.source_data,
                is_primary: params.is_primary,
                sort_order: params.sort_order,
                save_as_default: params.save_as_default,
            }),
        });
    }
    async updateMedia(mediaId, params) {
        return this.request(`/media/${mediaId}`, {
            method: 'PUT',
            body: JSON.stringify(params),
        });
    }
    async deleteMedia(mediaId) {
        return this.request(`/media/${mediaId}`, { method: 'DELETE' });
    }
    // Google Places API - Find nearby places
    async getPlaces(params) {
        return this.request('/places', {
            method: 'POST',
            body: JSON.stringify(params),
        });
    }
    // Google Distance Matrix API - Calculate driving distances
    async getDistances(params) {
        return this.request('/distances', {
            method: 'POST',
            body: JSON.stringify(params),
        });
    }
    // SEO Pages
    async getSEOPages(params) {
        const searchParams = new URLSearchParams();
        if (params?.limit)
            searchParams.set('limit', params.limit.toString());
        const query = searchParams.toString();
        return this.request(`/seo/pages${query ? `?${query}` : ''}`);
    }
    // Update an SEO page across all geography levels. The route shape differs
    // by entity type — neighborhoods use the historical bare-id form, regions
    // and districts use a type-prefixed path.
    async updateSEOPage(entityId, updates, options) {
        const entityType = options?.entityType ?? 'neighborhood';
        const path = entityType === 'neighborhood'
            ? `/seo/pages/${encodeURIComponent(entityId)}`
            : `/seo/pages/${entityType}/${encodeURIComponent(entityId)}`;
        return this.request(path, {
            method: 'PUT',
            body: JSON.stringify(updates),
        });
    }
    async getSEOPagePreview(pageId) {
        return this.request(`/seo/pages/${encodeURIComponent(pageId)}/preview`);
    }
    // All search queries for this agent, joined to the page they came from.
    // Used by the site-wide Search Queries panel.
    async getAllSearchQueries() {
        return this.request('/seo/queries');
    }
    // Site-wide GSC refresh: one API call against sc-domain:slowmaui.com,
    // attributes each (query, page) row to a seo_pages row via flexible URL
    // matching, and updates both seo_pages headline stats AND seo_page_queries
    // in one pass. Returns a summary so the UI can surface unattributed URLs
    // (legacy / stranded pages still drawing impressions).
    async refreshAllSearchConsole() {
        return this.request('/seo/queries/refresh', { method: 'POST' });
    }
    // Per-page GSC URL Inspection state — tells us which published pages
    // Google has actually indexed vs ones stuck in "Discovered – currently
    // not indexed" or similar dead-end states. The single most diagnostic
    // signal for a young SEO surface that isn't ranking.
    async getCoverage() {
        return this.request('/seo/coverage');
    }
    // Refresh GSC coverage for one page at a time. Caller iterates over the
    // published-page list with progress (CF Workers' 30s wall-clock limit
    // can't accommodate inspecting 30+ pages in a single request).
    async refreshCoverageOne(pageId) {
        return this.request('/seo/coverage/refresh', {
            method: 'POST',
            body: JSON.stringify({ pageId }),
        });
    }
    // Re-run the publish path for every currently-published SEO page (across
    // neighborhood + region + district) for this agent. Use after a deploy that
    // changes the SEO page template so existing R2 HTML picks up the new shell.
    async republishAllSeoPages() {
        return this.request('/seo/pages/republish-all', { method: 'POST' });
    }
    async getSEOPageQueries(pageId) {
        return this.request(`/seo/pages/${encodeURIComponent(pageId)}/queries`);
    }
    // Onboarding
    async checkSubdomain(subdomain) {
        return this.request(`/auth/subdomain-check?subdomain=${encodeURIComponent(subdomain)}`);
    }
}
//# sourceMappingURL=agent.js.map